import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Reveal from "../components/Reveal";
import {
  data,
  graphicDesgignData,
  sculptureData,
  paintingData,
  portraitData,
  wallArtData,
} from "../data";

const FILTERS = [
  { key: "wallart", label: "Wall Art" },
  { key: "all", label: "All work" },
  { key: "mural", label: "Mural Arts" },
  { key: "graphic", label: "Graphic Design" },
  { key: "sculpture", label: "Sculpture" },
  { key: "painting", label: "Painting" },
  { key: "portrait", label: "Portrait" },
  // { key: "wallart", label: "Wall Art" },
];

const buildItems = () => {
  const items = [];
  data.forEach((p) =>
    items.push({
      id: p.id,
      type: "mural",
      title: p.title,
      img: p.img1,
      href: `/ourwork/muralarts/${p.id}`,
    })
  );
  graphicDesgignData.forEach((p) =>
    items.push({
      id: `g-${p.id}`,
      type: "graphic",
      title: p.title,
      img: p.img1,
      href: `/ourwork/graphicwork/${p.id}`,
    })
  );
  sculptureData.forEach((p) =>
    items.push({
      id: `s-${p.id}`,
      type: "sculpture",
      title: p.title,
      img: p.img1,
      href: `/ourwork/sculpturework/${p.id}`,
    })
  );
  paintingData.forEach((p) =>
    items.push({
      id: `pt-${p.id}`,
      type: "painting",
      title: p.title,
      img: p.img1,
      href: `/ourwork/paintingwork/${p.id}`,
    })
  );
  portraitData.forEach((p) =>
    items.push({
      id: `pr-${p.id}`,
      type: "portrait",
      title: p.title,
      img: p.img1,
      href: `/ourwork/portraitwork/${p.id}`,
    })
  );
  wallArtData.forEach((p) =>
    items.push({
      id: `w-${p.id}`,
      type: "wallart",
      title: p.title,
      img: p.img1,
      href: `/ourwork/wallart/${p.id}`,
    })
  );
  return items;
};

// Pseudo-random aspect ratios for organic masonry feel
const ASPECTS = [
  "aspect-[4/5]",
  "aspect-[3/4]",
  "aspect-[1/1]",
  "aspect-[4/3]",
  "aspect-[5/4]",
  "aspect-[16/10]",
  "aspect-[2/3]",
  "aspect-[3/2]",
];

export default function Gallery() {
  const [filter, setFilter] = useState("wallart"); // all | mural | graphic | sculpture | painting | portrait
  const [view, setView] = useState("masonry"); // masonry | grid
  const location = useLocation();

  // Support ?type=graphic (etc.) from Work page deep-link
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("type");
    if (t && FILTERS.some((f) => f.key === t)) {
      setFilter(t);
    }
  }, [location.search]);

  const items = useMemo(() => buildItems(), []);
  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((i) => i.type === filter)),
    [filter, items]
  );

  // Counts per type
  const counts = useMemo(() => {
    const c = { all: items.length };
    items.forEach((i) => (c[i.type] = (c[i.type] || 0) + 1));
    return c;
  }, [items]);

  return (
    <main className="bg-[var(--bg)] text-[var(--bone)] pt-32 pb-24">
      {/* HERO */}
      <section className="px-5 md:px-10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-[var(--muted)] mb-6">
              <span className="w-8 h-px bg-[var(--clay)]" />
              The Wall Index · {counts.all} works
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display font-light text-[clamp(3rem,9vw,8rem)] leading-[0.92] tracking-tight">
              The <span className="italic font-extralight">complete</span>{" "}
              <span className="text-[var(--clay)]">gallery.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-8 max-w-2xl text-[var(--muted)] text-[17px] leading-relaxed">
              Every wall, every canvas, every sculpture we've put our hands on
              — laid out for you to wander. Filter by practice, take your time.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Sticky filter bar */}
      <section className="sticky top-[68px] z-30 bg-[rgba(10,9,8,0.78)] backdrop-blur-xl border-y border-[var(--line)] mt-16">
        <div className="px-5 md:px-10 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-1 md:gap-2 overflow-x-auto no-scrollbar">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`relative px-3 md:px-4 py-2 text-xs uppercase tracking-[0.2em] whitespace-nowrap transition-colors ${
                  filter === f.key
                    ? "text-[var(--bone)]"
                    : "text-[var(--muted)] hover:text-[var(--bone)]"
                }`}
              >
                {f.label}
                <span className="ml-1.5 text-[10px] text-[var(--clay)] tabular-nums">
                  {counts[f.key] || 0}
                </span>
                {filter === f.key && (
                  <span className="absolute left-3 right-3 -bottom-[14px] h-px bg-[var(--clay)]" />
                )}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[var(--muted)]">
            <button
              onClick={() => setView("masonry")}
              className={`px-2 py-1 ${
                view === "masonry"
                  ? "text-[var(--bone)]"
                  : "hover:text-[var(--bone)]"
              }`}
            >
              Masonry
            </button>
            <span className="text-[var(--line-strong)]">/</span>
            <button
              onClick={() => setView("grid")}
              className={`px-2 py-1 ${
                view === "grid"
                  ? "text-[var(--bone)]"
                  : "hover:text-[var(--bone)]"
              }`}
            >
              Grid
            </button>
          </div>
        </div>
      </section>

      {/* MASONRY */}
      {view === "masonry" && (
        <section className="px-5 md:px-10 mt-12">
          <div className="max-w-7xl mx-auto columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 md:gap-6 [column-fill:_balance]">
            {filtered.map((it, i) => (
              <Reveal key={it.id} delay={(i % 8) * 60}>
                <Link
                  to={it.href}
                  className="group block mb-4 md:mb-6 break-inside-avoid relative overflow-hidden bg-[var(--bg-elevated)]"
                >
                  <div className={`${ASPECTS[i % ASPECTS.length]} overflow-hidden`}>
                    <img
                      src={it.img}
                      alt={it.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                    />
                  </div>
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,9,8,0.85)] via-transparent to-transparent opacity-100" />

                  {/* Top tag */}
                  <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-2.5 py-1 bg-[rgba(10,9,8,0.7)] backdrop-blur-md rounded-full text-[9px] uppercase tracking-[0.28em] text-[var(--bone)]">
                    <span className="w-1 h-1 rounded-full bg-[var(--clay)]" />
                    {it.type}
                  </div>

                  {/* Bottom caption */}
                  <div className="absolute left-0 right-0 bottom-0 p-4 md:p-5 flex items-end justify-between translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.32em] text-[var(--bone)]/60">
                        0{(i + 1).toString().padStart(2, "0")}
                      </div>
                      <h3 className="font-display text-lg md:text-xl text-[var(--bone)] leading-tight">
                        {it.title}
                      </h3>
                    </div>
                    <span className="w-9 h-9 grid place-items-center rounded-full border border-[var(--bone)]/60 text-[var(--bone)] -rotate-45 group-hover:rotate-0 group-hover:bg-[var(--clay)] group-hover:border-[var(--clay)] transition-all duration-500">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M13 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-24 text-[var(--muted)]">
              Nothing here yet — but it's coming.
            </div>
          )}
        </section>
      )}

      {/* GRID */}
      {view === "grid" && (
        <section className="px-5 md:px-10 mt-12">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filtered.map((it, i) => (
              <Reveal key={it.id} delay={(i % 8) * 50}>
                <Link
                  to={it.href}
                  className="group block relative overflow-hidden bg-[var(--bg-elevated)] aspect-[4/5]"
                >
                  <img
                    src={it.img}
                    alt={it.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-[var(--ink)]/30 group-hover:bg-[var(--ink)]/50 transition-colors duration-500" />
                  <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-2.5 py-1 bg-[rgba(10,9,8,0.7)] backdrop-blur-md rounded-full text-[9px] uppercase tracking-[0.28em] text-[var(--bone)]">
                    <span className="w-1 h-1 rounded-full bg-[var(--clay)]" />
                    {it.type}
                  </div>
                  <div className="absolute left-0 right-0 bottom-0 p-4">
                    <h3 className="font-display text-lg text-[var(--bone)] leading-tight">
                      {it.title}
                    </h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Footer line */}
      <section className="px-5 md:px-10 mt-24">
        <div className="max-w-7xl mx-auto border-t border-[var(--line)] pt-12 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
              — End of gallery
            </div>
            <h3 className="mt-3 font-display text-3xl md:text-5xl text-[var(--bone)] leading-tight max-w-xl">
              See a wall you'd like us to paint next?
            </h3>
          </div>
          <Link
            to="/booknow"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-[var(--clay)] text-[var(--bone)] rounded-full tracking-wide hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors"
          >
            Commission a wall
            <span className="relative w-4 h-4 overflow-hidden">
              <span className="absolute inset-0 transition-transform duration-500 group-hover:translate-x-5 group-hover:-translate-y-5">→</span>
              <span className="absolute inset-0 -translate-x-5 translate-y-5 transition-transform duration-500 group-hover:translate-x-0 group-hover:translate-y-0">→</span>
            </span>
          </Link>
        </div>
      </section>
    </main>
  );
}